import {
  Box,
  Button,
  Chip,
  Link,
  Modal,
  Stack,
  Text,
} from '@ltvco/refresh-lib/theme';
import { CampaignOutlined, EventBusyOutlined } from '@mui/icons-material';

interface DeprecationModalProps {
  open: boolean;
  onClose: () => void;
}

export const DeprecationModal = ({ open, onClose }: DeprecationModalProps) => {
  const close = () => onClose();

  return (
    <Modal
      title=""
      open={open}
      onClose={close}
      hasCloseIcon
      actionsProps={{ sx: { px: 3, pb: 3, pt: 1.5 } }}
      actions={[
        <Button
          key="got-it"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={close}
        >
          Got it
        </Button>,
      ]}
    >
      <Box
        sx={(theme) => ({
          borderLeft: `4px solid ${theme.palette.warning.main}`,
          pl: { xs: 2, sm: 3 },
          pr: { xs: 1, sm: 2 },
          py: 1,
          mt: 0.5,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        })}
      >
        <Stack spacing={2.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Chip
              icon={<CampaignOutlined sx={{ fontSize: 16 }} />}
              label="Deprecation Notice"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={(theme) => ({ color: theme.palette.text.secondary })}
            >
              <EventBusyOutlined sx={{ fontSize: 16 }} />
              <Text variant="body2" sx={{ fontWeight: 600 }}>
                August 24, 2026
              </Text>
            </Stack>
          </Stack>

          <Text
            variant="h2"
            sx={{ fontWeight: 700, letterSpacing: '-0.01em', m: 0 }}
          >
            ID Monitor is being deprecated
          </Text>

          <Stack spacing={1.5}>
            <Text variant="body1">
              On August 24, 2026, BeenVerified will no longer support ID Monitor
              and its identity theft and credit protection tools.
            </Text>
            <Text variant="body1">
              Enrolled users will be un-enrolled from TransUnion and programs
              entered through BeenVerified&rsquo;s ID Monitor feature.
            </Text>
          </Stack>

          <Stack spacing={1}>
            <Text
              variant="overline"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                fontWeight: 700,
                letterSpacing: '0.08em',
              })}
            >
              What&rsquo;s changing?
            </Text>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                '& li': { marginBottom: 0.5 },
                '& li:last-of-type': { marginBottom: 0 },
              }}
            >
              <li>
                <Text variant="body1" component="span">
                  The ID Monitor feature enabled you to view your TransUnion
                  credit data and access identity theft and credit protection
                  tools.
                </Text>
              </li>
              <li>
                <Text variant="body1" component="span">
                  The feature will no longer be available after August 24, 2026.
                </Text>
              </li>
            </Box>
          </Stack>

          <Stack spacing={1}>
            <Text
              variant="overline"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                fontWeight: 700,
                letterSpacing: '0.08em',
              })}
            >
              What&rsquo;s not changing?
            </Text>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                '& li': { marginBottom: 0.5 },
                '& li:last-of-type': { marginBottom: 0 },
              }}
            >
              <li>
                <Text variant="body1" component="span">
                  Your subscription price will remain the same.
                </Text>
              </li>
              <li>
                <Text variant="body1" component="span">
                  All other BeenVerified features and services will continue to
                  be available.
                </Text>
              </li>
            </Box>
          </Stack>

          <Stack spacing={1}>
            <Text variant="body1">
              If you&rsquo;d prefer not to continue your subscription after this
              change, you may cancel at any time by:
            </Text>
            <Box
              component="ol"
              sx={{
                pl: 3,
                m: 0,
                '& li': { marginBottom: 0.5 },
                '& li:last-of-type': { marginBottom: 0 },
              }}
            >
              <li>
                <Text variant="body1" component="span">
                  Logging into your account
                </Text>
              </li>
              <li>
                <Text variant="body1" component="span">
                  Navigating to{' '}
                  <Link
                    href="https://www.beenverified.com/rf/dashboard/billing"
                    underline="hover"
                    sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  >
                    https://www.beenverified.com/rf/dashboard/billing
                  </Link>
                </Text>
              </li>
              <li>
                <Text variant="body1" component="span">
                  Go to &ldquo;Account Information&rdquo; and click &ldquo;Edit
                  Account Information&rdquo;
                </Text>
              </li>
              <li>
                <Text variant="body1" component="span">
                  Select &ldquo;Cancel My Account&rdquo; option and proceed
                </Text>
              </li>
            </Box>
          </Stack>

          <Text variant="body1">
            If you have any questions, please contact us at{' '}
            <Link
              href="mailto:support@beenverified.com"
              underline="hover"
              sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
            >
              support@beenverified.com
            </Link>
            .
          </Text>

          <Text variant="body1" sx={{ fontWeight: 600 }}>
            Thank you for being a BeenVerified customer.
          </Text>
        </Stack>
      </Box>
    </Modal>
  );
};
